import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { format } from 'date-fns'
import { motion, AnimatePresence } from 'framer-motion'
import { Loader2, Plus, Trash2 } from 'lucide-react'
import { toast } from 'sonner'
import {
  useMyCredentials,
  useCreateMyCredential,
  useDeleteMyCredential,
} from '@/entities/credential'
import type { EmployeeCredential } from '@/entities/credential'
import { Button } from '@/shared/components/ui/button'
import { Badge } from '@/shared/components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/shared/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/shared/components/ui/form'
import { Input } from '@/shared/components/ui/input'
import { Textarea } from '@/shared/components/ui/textarea'

const addCredentialSchema = z.object({
  credentialName: z.string().min(1, 'Name is required'),
  credentialNumber: z.string().optional(),
  issuingAuthority: z.string().optional(),
  issuedOn: z.string().optional(),
  expiresOn: z.string().optional(),
  notes: z.string().optional(),
})

type AddCredentialValues = z.infer<typeof addCredentialSchema>

const defaultAddValues: AddCredentialValues = {
  credentialName: '',
  credentialNumber: '',
  issuingAuthority: '',
  issuedOn: '',
  expiresOn: '',
  notes: '',
}

function formatDate(value: string | null): string | null {
  if (!value) return null
  try {
    return format(new Date(value), 'MMM d, yyyy')
  } catch {
    return null
  }
}

interface CredentialRowProps {
  credential: EmployeeCredential
  onDelete: (id: number) => void
  isDeleting: boolean
}

function CredentialRow({ credential, onDelete, isDeleting }: CredentialRowProps) {
  const [confirmOpen, setConfirmOpen] = useState(false)

  const expiryLabel = formatDate(credential.expiresOn)

  return (
    <>
      <div className="flex items-start justify-between gap-3 rounded-xl border border-line bg-surface px-4 py-3">
        <div className="min-w-0 flex-1 space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-medium text-ink">{credential.credentialName}</span>
            {credential.isExpired && (
              <Badge tone="bad">Expired</Badge>
            )}
            {!credential.isExpired && credential.isExpiringSoon && (
              <Badge tone="warn">Expiring soon</Badge>
            )}
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-ink-3">
            {credential.credentialNumber && (
              <span>#{credential.credentialNumber}</span>
            )}
            {expiryLabel && (
              <span>Expires {expiryLabel}</span>
            )}
          </div>
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 shrink-0 text-ink-3 hover:text-bad"
          onClick={() => setConfirmOpen(true)}
          disabled={isDeleting}
          title="Delete credential"
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete credential</DialogTitle>
            <DialogDescription>
              Remove <strong>{credential.credentialName}</strong> from your profile? This
              cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConfirmOpen(false)}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={isDeleting}
              onClick={() => {
                onDelete(credential.id)
                setConfirmOpen(false)
              }}
            >
              {isDeleting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

interface AddCredentialModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

function AddCredentialModal({ open, onOpenChange }: AddCredentialModalProps) {
  const createMutation = useCreateMyCredential()

  const form = useForm<AddCredentialValues>({
    resolver: zodResolver(addCredentialSchema),
    defaultValues: defaultAddValues,
  })

  const handleSubmit = form.handleSubmit(async (values) => {
    try {
      await createMutation.mutateAsync({
        credentialName: values.credentialName.trim(),
        credentialNumber: values.credentialNumber?.trim() || null,
        issuingAuthority: values.issuingAuthority?.trim() || null,
        issuedOn: values.issuedOn?.trim() || null,
        expiresOn: values.expiresOn?.trim() || null,
        notes: values.notes?.trim() || null,
      })
      form.reset(defaultAddValues)
      onOpenChange(false)
    } catch {
      toast.error('Failed to add credential')
    }
  })

  const handleOpenChange = (next: boolean) => {
    if (!next) form.reset(defaultAddValues)
    onOpenChange(next)
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Add credential</DialogTitle>
          <DialogDescription>
            Enter your license or certification details. You can attach a document later.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={handleSubmit} className="space-y-4">
            <FormField
              control={form.control}
              name="credentialName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Credential name *</FormLabel>
                  <FormControl>
                    <Input placeholder="e.g. Security Guard License" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="credentialNumber"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Credential number</FormLabel>
                  <FormControl>
                    <Input placeholder="Optional" {...field} value={field.value ?? ''} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="issuingAuthority"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Issuing authority</FormLabel>
                  <FormControl>
                    <Input placeholder="Optional" {...field} value={field.value ?? ''} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-3">
              <FormField
                control={form.control}
                name="issuedOn"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Issued on</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} value={field.value ?? ''} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="expiresOn"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Expires on</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} value={field.value ?? ''} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Notes</FormLabel>
                  <FormControl>
                    <Textarea
                      rows={2}
                      placeholder="Optional"
                      {...field}
                      value={field.value ?? ''}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => handleOpenChange(false)}
                disabled={createMutation.isPending}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={createMutation.isPending}>
                {createMutation.isPending && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                Add credential
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}

export function CredentialsManager() {
  const { data: credentials, isLoading } = useMyCredentials()
  const deleteMutation = useDeleteMyCredential()
  const [addOpen, setAddOpen] = useState(false)

  const list = credentials ?? []

  return (
    <div className="mt-8 border-t border-line pt-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h2 className="text-base font-semibold text-ink">Your credentials</h2>
          <p className="mt-0.5 text-sm text-ink-3">
            Add your licenses and certifications now so they are ready when a team invites you.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          className="shrink-0 rounded-xl border-line font-medium hover:bg-hover"
          onClick={() => setAddOpen(true)}
        >
          <Plus className="mr-1.5 h-4 w-4" />
          Add credential
        </Button>
      </div>

      <div className="mt-4">
        {isLoading ? (
          <div className="flex items-center gap-2 py-4 text-sm text-ink-3">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading...
          </div>
        ) : list.length === 0 ? (
          <p className="rounded-xl border border-dashed border-line py-6 text-center text-sm text-ink-3">
            No credentials yet. Add one to get started.
          </p>
        ) : (
          <motion.div
            className="max-h-64 space-y-2 overflow-y-auto pr-1"
            initial={false}
          >
            <AnimatePresence initial={false}>
              {list.map((credential) => (
                <motion.div
                  key={credential.id}
                  initial={{ opacity: 0, y: -6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -6 }}
                  transition={{ duration: 0.18 }}
                >
                  <CredentialRow
                    credential={credential}
                    onDelete={(id) => deleteMutation.mutate(id)}
                    isDeleting={deleteMutation.isPending}
                  />
                </motion.div>
              ))}
            </AnimatePresence>
          </motion.div>
        )}
      </div>

      <AddCredentialModal open={addOpen} onOpenChange={setAddOpen} />
    </div>
  )
}
